export type CpmOverrideCampaignConfig = {
  cpm_rate_cents: number;
  allow_cpm_override?: boolean | null;
  cpm_override_mode?: string | null;
  cpm_override_presets_cents?: (number | null)[] | null;
  cpm_override_min_cents?: number | null;
  cpm_override_max_cents?: number | null;
};

export type CpmOverrideEditorConfig =
  | { canEdit: false }
  | {
      canEdit: true;
      /** Preset buttons to show ([] when none configured). */
      presetsCents: number[];
      /** Free-value input constraints, or false when only presets are allowed. */
      freeInput: false | { minCents: number; maxCents: number | null };
    };

export function effectiveCpmRateCents(
  campaign: Pick<CpmOverrideCampaignConfig, 'cpm_rate_cents'>,
  overrideCents: number | null | undefined,
): number {
  return Number.isInteger(overrideCents) && (overrideCents as number) >= 0
    ? (overrideCents as number)
    : campaign.cpm_rate_cents;
}

/**
 * Derive what the audit page's CPM editor should offer. Mirrors the backend's
 * validateCpmOverride constraints for UX only — the backend stays the
 * authority. Platform admins are never constrained: they always get an
 * unbounded free input, with any configured presets kept as shortcuts.
 */
export function getCpmOverrideEditorConfig({
  campaign,
  isPlatformAdmin,
}: {
  campaign: CpmOverrideCampaignConfig | null | undefined;
  isPlatformAdmin: boolean;
}): CpmOverrideEditorConfig {
  if (!campaign) return { canEdit: false };

  const allowed = !!campaign.allow_cpm_override;
  const mode = campaign.cpm_override_mode === 'free' ? 'free' : 'presets';
  const presetsCents = (campaign.cpm_override_presets_cents || []).filter(
    (v): v is number => Number.isInteger(v) && (v as number) >= 0,
  );

  if (isPlatformAdmin) {
    return {
      canEdit: true,
      presetsCents: allowed && mode === 'presets' ? presetsCents : [],
      freeInput: { minCents: 0, maxCents: null },
    };
  }

  if (!allowed) return { canEdit: false };

  if (mode === 'presets') {
    if (presetsCents.length === 0) return { canEdit: false };
    return { canEdit: true, presetsCents, freeInput: false };
  }

  return {
    canEdit: true,
    presetsCents: [],
    freeInput: {
      minCents: campaign.cpm_override_min_cents ?? 0,
      maxCents: campaign.cpm_override_max_cents ?? null,
    },
  };
}
